import {
    attachmentService,
    type AttachmentVO,
    type UploadAttachmentBO,
} from '@/services/auditService/attachment';
import type { RichTextUploadContext } from '@/pages/components/common/richText/types';

/** Strip transient blob:/data: src so persisted HTML only keeps data-file-key. */
export function toPersistHtml(html: string): string {
    if (!html) return '';
    if (typeof DOMParser === 'undefined') return html;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    doc.querySelectorAll('img').forEach((img) => {
        const key = img.getAttribute('data-file-key');
        if (key) {
            const src = img.getAttribute('src') || '';
            if (src.startsWith('blob:') || src.startsWith('data:')) {
                // Keep empty src attribute so TipTap can still round-trip before hydrate
                // (default Image parseHTML requires [src]; FileKeyImage also matches data-file-key).
                img.setAttribute('src', '');
            }
        }
    });
    return doc.body.innerHTML;
}

/** True when HTML has MinIO-backed images that still need blob URL hydration. */
export function hasUnhydratedFileKeyImages(html: string): boolean {
    if (!html || typeof DOMParser === 'undefined') return false;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(doc.querySelectorAll('img')).some((img) => {
        const fileKey = img.getAttribute('data-file-key');
        if (!fileKey) return false;
        const src = (img.getAttribute('src') || '').trim();
        return !(src.startsWith('blob:') || src.startsWith('http'));
    });
}

export function revokeObjectUrls(urls: string[]): void {
    urls.forEach((u) => {
        try {
            URL.revokeObjectURL(u);
        } catch {
            // ignore
        }
    });
}

/**
 * Resolve data-file-key images to object URLs for display.
 * Appends created URLs to `objectUrlsOut` for later revoke.
 */
export async function hydrateInlineImagesHtml(
    html: string,
    ctx: Pick<RichTextUploadContext, 'companyId' | 'annualId'> | undefined,
    objectUrlsOut: string[],
): Promise<string> {
    if (!html || typeof DOMParser === 'undefined') return html || '';
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const imgs = Array.from(doc.querySelectorAll('img'));
    for (const img of imgs) {
        const fileKey = img.getAttribute('data-file-key');
        if (!fileKey) continue;
        const existing = img.getAttribute('src') || '';
        if (existing.startsWith('blob:') || existing.startsWith('http')) continue;
        try {
            const blob = await attachmentService.fetchAttachmentBlob({
                companyId: ctx?.companyId,
                annualId: ctx?.annualId,
                fileKey,
            });
            const url = URL.createObjectURL(blob);
            objectUrlsOut.push(url);
            img.setAttribute('src', url);
        } catch {
            // leave without src; broken image placeholder
        }
    }
    return doc.body.innerHTML;
}

export function isImageFile(file: File): boolean {
    if (file.type && file.type.startsWith('image/')) return true;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || '');
}

export function canUpload(ctx: RichTextUploadContext | undefined): ctx is RichTextUploadContext {
    return !!ctx && typeof ctx.targetId === 'number' && ctx.targetId > 0
        && typeof ctx.userId === 'number' && !!ctx.targetType;
}

export function attachmentTargetTypeOf(ctx: RichTextUploadContext): string {
    return ctx.attachmentTargetType || ctx.targetType;
}

export function toUploadBo(
    ctx: RichTextUploadContext,
    targetType: string,
): UploadAttachmentBO {
    return {
        targetType,
        targetId: ctx.targetId,
        userId: ctx.userId,
        companyId: ctx.companyId,
        annualId: ctx.annualId,
    };
}

/** Upload files then return newly created attachment rows (by id diff). */
export async function uploadAndFindNew(
    files: File[],
    bo: UploadAttachmentBO,
): Promise<AttachmentVO[]> {
    const before = await attachmentService.listAttachments(bo.targetType, bo.targetId);
    const beforeIds = new Set(before.map((a) => a.id));
    const result = await attachmentService.batchUploadAttachments(files, bo);
    if (result.errors.length > 0 && result.successCount === 0) {
        throw new Error(result.errors.join('\n') || 'Upload failed');
    }
    const after = await attachmentService.listAttachments(bo.targetType, bo.targetId);
    const created = after.filter((a) => !beforeIds.has(a.id));
    if (created.length === 0 && result.successCount > 0) {
        // fallback: newest by uploadedAt
        return after
            .slice()
            .sort((a, b) => String(b.uploadedAt).localeCompare(String(a.uploadedAt)))
            .slice(0, result.successCount);
    }
    return created;
}

export function formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}
